import React, { useRef, useState } from 'react';
import { Modal } from '@sabre/spark-react-core';
import Button from '@sabre/spark-react-core/button';
import Icon from '@sabre/spark-react-core/icon';
import TextInput from '@sabre/spark-react-core/text-input';
import { bool, func, string } from 'prop-types';
import { ERROR_MESSAGE_LIMIT } from '../../utils/constants.js';
import { NOTIFICATION_MESSAGES } from '../../utils/notification-messages';
import { toastWarn } from '../../utils/toasts';
import NewFaresError from '../core-pricing-component/new-fares-error/new-fares-error.jsx';
import { uploadFile } from './__helpers__/functions';

const UploadFileModal = ({
   title = '',
   alertPaneTitle = '',
   generalPaneTitle = '',
   uploadIdTitle = '',
   uploadId = '',
   onClose = () => {},
   onUploadIdChange = () => {},
   showUploadIdInput = false,
   requestType = '',
   errorPopupTitle = '',
}) => {
   const uploadFileRef = useRef(null);
   const [file, setFile] = useState(null);
   const [errors, setErrors] = useState([]);
   const [successRecordCount, setSuccessRecordCount] = useState(0);
   const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
   const successCountSuffix = successRecordCount > 1 ? 's' : '';
   const successCountHelpingVerb = successRecordCount > 1 ? 'were' : 'was';
   const successMessage = `There ${successCountHelpingVerb} ${successRecordCount} record${successCountSuffix} successfully updated.`;

   const handleCancel = () => {
      onClose();
   };

   const handleFileChange = async (event) => {
      const file = event.target.files[0];
      setFile(file);
   };

   const handleSubmit = (event) => {
      event.preventDefault();

      if (!file) {
         toastWarn(NOTIFICATION_MESSAGES.INVALID_CSV_FILE);
         return;
      }

      uploadFile(file, requestType, uploadId)
         .then((response) => {
            const errors = Object.keys(response?.data?.errorMessages);

            if (errors?.length > 0) {
               const errorMessages = errors?.map((key) => {
                  return {
                     row: key,
                     message: response.data.errorMessages[key],
                  };
               });

               setErrors(errorMessages);

               if (errors?.length > ERROR_MESSAGE_LIMIT)
                  setIsErrorModalOpen(true);
            }
            if (response?.data?.persistedRecordsCount > 0) {
               setSuccessRecordCount(response.data.persistedRecordsCount);
            }
         })
         .catch((error) => {
            toastWarn(error);
            console.warn(error);
         });
   };

   return (
      <div className='upload-modal-root'>
         <form onSubmit={handleSubmit}>
            <section className='row'>
               <h1 className='spark-bold spark-large spark-mar-t-0'>{title}</h1>
            </section>

            <section className='spark-mar-t-1 spark-pad-.5 upload-modal-info-panel'>
               <div className='spark-flex'>
                  <div className='spark-flex upload-modal-info-icon-circle alignment-center'>
                     <Icon
                        name='alert-triangle'
                        size='md'
                        style='fill'
                        label='alert'
                     />
                  </div>

                  <h2 className='spark-mar-l-1 spark-mar-t-0 upload-modal-root-alert-text'>
                     {alertPaneTitle}
                  </h2>
               </div>

               <div>
                  {errors?.length <= ERROR_MESSAGE_LIMIT && (
                     <div className='spark-mar-t-1'>
                        <ul>
                           {errors.map(({ row, message }) => (
                              <li key={row}>{`Row ${row} - ${message}`}</li>
                           ))}
                        </ul>
                     </div>
                  )}
               </div>
            </section>

            <section className='spark-mar-t-1 spark-pad-.5 upload-modal-info-panel'>
               <div className='spark-flex'>
                  <div className='upload-modal-info-icon-circle upload-modal-root-general-text' />
                  <h2 className='spark-mar-l-1 spark-mar-t-0 upload-modal-root-alert-text upload-modal-root-general-text-color'>
                     {generalPaneTitle}
                  </h2>
               </div>
               <p className='spark-mar-.5'>
                  {successRecordCount > 0 && successMessage}
               </p>
            </section>

            {showUploadIdInput && (
               <div className='row spark-mar-top-2 spark-bold'>
                  <h3 className='upload-modal-label spark-mar-t-0'>
                     {uploadIdTitle}
                  </h3>
                  <TextInput
                     name='Upload Id'
                     value={uploadId}
                     onChange={(event, changedValue) =>
                        onUploadIdChange(event, changedValue)
                     }
                  />
               </div>
            )}

            <div className='row spark-pad-t-2'>
               <input
                  ref={uploadFileRef}
                  name='file'
                  type='file'
                  accept='.csv'
                  required
                  onChange={handleFileChange}
                  aria-label='File Upload Input'
               />
            </div>

            <div className='row spark-flex spark-pad-t-2 alignment-right'>
               <Button
                  name='Cancel Button'
                  className='spark-btn--secondary spark-mar-r-1'
                  size={Button.SIZE.SMALL}
                  onClick={handleCancel}
                  aria-label='Upload File Cancel Button'
               >
                  CANCEL
               </Button>
               <Button
                  name='Upload Button'
                  className='spark-btn--primary'
                  size={Button.SIZE.SMALL}
                  onClick={handleSubmit}
                  type='submit'
               >
                  UPLOAD
               </Button>
            </div>
         </form>

         <div className='delete-popup'>
            <Modal
               onClose={() => setIsErrorModalOpen(false)}
               open={isErrorModalOpen}
               titleHeadingLevel={3}
               modalId='file-upload-errors-modal'
               contentClassName='col-xs-12 col-sm-12 col-md-11 col-lg-8 col-xl-6'
            >
               <NewFaresError
                  data={errors?.map((error) => [[error.row], [error.message]])}
                  onClose={() => setIsErrorModalOpen(false)}
                  title={`There were ${errors?.length} errors when attempting to upload. ${errorPopupTitle}`}
                  fileName='Market List Error Report'
               />
            </Modal>
         </div>
      </div>
   );
};

UploadFileModal.propTypes = {
   title: string,
   alertPaneTitle: string,
   generalPaneTitle: string,
   uploadIdTitle: string,
   uploadId: string,
   onClose: func,
   onUploadIdChange: func,
   showUploadIdInput: bool,
   requestType: string,
   errorPopupTitle: string,
};

export default UploadFileModal;
